import React, { useEffect, useRef, useState } from "react";
import { Flex, Input, InputRef, Typography } from "antd";
import { toast } from "react-toastify";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { signInWithPhoneNumber } from "firebase/auth";

import { useOtpPaste } from "../../../../../hooks/useOtpPaste";
import { customLogger } from "../../../../../utils/Logger";
import { auth } from "../../../../../firebase/firebase";
import { storageService } from "../../../../../services/storageService";
import apiInstance from "../../../../../services/apiService";

import "./index.scss";

const { Text, Title } = Typography;

const VerifyRegisterOTP = ({
  phoneNumber,
  handleBackClick,
  otpSentFromBackend = false,
}: {
  phoneNumber: string;
  handleBackClick: () => void;
  otpSentFromBackend: boolean;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(59);
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const inputRefs = useRef<InputRef[]>([]);
  const isOtpSubmitting = useRef(false);

  const verifyIdToken = async (idToken: string, code?: string) => {
    setLoading(true);

    const url = `auth/auth/registerNumber`;

    const payload: Record<string, any> = {
      ...(otpSentFromBackend
        ? { phoneNumber, otp: code }
        : idToken
          ? { idToken }
          : {}),
    };

    try {
      const response = await apiInstance.post(url, payload);

      if (response.status === 200) {
        toast.success("Verification successful");
        storageService.setIsNumberRegistered(true);
        window.location.reload();
      } else {
        console.error("Failed to verify:", response.data);
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      toast.error(
        error?.response?.data?.message ||
          "There was an error. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOtp = async (code: string[]) => {
    const accessCode = code.join("");
    setLoading(true);

    try {
      if (otpSentFromBackend) {
        const url = "auth/app/verifyForNumber";
        const response = await apiInstance.post(url, {
          phoneNumber,
          otp: accessCode,
        });

        if (response.status === 200) {
          await verifyIdToken("", accessCode);
        }
      } else {
        const confirmationResult = window.confirmationResult;

        if (!confirmationResult) {
          toast.error(
            "❌ Confirmation result is not available. Page has been refreshed. Please try again",
          );
          handleBackClick();
          return;
        }

        const result = await confirmationResult.confirm(accessCode);

        if (result.user) {
          const tokenId = await result.user.getIdToken();
          await verifyIdToken(tokenId);
        }
      }
    } catch (error: any) {
      console.error("OTP verification error:", error);
      toast.error(error?.response?.data?.message || "There was an error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (e.target.value && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    const isOtpComplete = newOtp.every((digit) => digit !== "");
    if (isOtpComplete) {
      handleSubmitOtp(newOtp);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "Enter") {
      const isOtpComplete = otp.every((digit) => digit !== "");
      if (isOtpComplete) {
        handleSubmitOtp(otp);
      }
    }
  };

  const { handlePaste } = useOtpPaste({
    loading,
    isSubmitting: isOtpSubmitting,
    inputRefs,
    setOtp,
    onComplete: handleSubmitOtp,
  });

  const resendOTPfromBackend = async () => {
    const url = "auth/app/sendOtpForNumber";

    try {
      const response = await apiInstance.post(url, { phoneNumber });
      if (response.status === 200) {
        toast.info("OTP has been sent successfully");
        setTimer(59);
        setOtp(new Array(6).fill(""));
        inputRefs.current[0]?.focus();
      }
    } catch (err: any) {
      customLogger("error sending OTP from Backend", err);
      toast.error(err.message || "There was an error. Please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (loading || timer > 0) return;

    setLoading(true);

    if (otpSentFromBackend) {
      await resendOTPfromBackend();
    } else {
      try {
        const appVerifier = window.recaptchaVerifier;

        if (!appVerifier) {
          toast.error("ReCAPTCHA verifier not initialized. Please refresh.");
          handleBackClick();
          return;
        }

        const confirmationResult = await signInWithPhoneNumber(
          auth,
          phoneNumber || "",
          appVerifier,
        );

        window.confirmationResult = confirmationResult;
        toast.success("OTP sent successfully!");
        setTimer(59);
        setOtp(new Array(6).fill(""));
        inputRefs.current[0]?.focus();
      } catch (error: any) {
        console.error("Error sending OTP:", error);
        toast.error(error.message || "Failed to send OTP");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (timer <= 0) return;

    const timeout = setTimeout(() => {
      setTimer(timer - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [timer]);

  // useEffect(() => {
  //   if (!window.recaptchaVerifier) {
  //     window.recaptchaVerifier = new RecaptchaVerifier(
  //       auth,
  //       "recaptcha-container",
  //       {
  //         size: "invisible",
  //       },
  //     );

  //     window.recaptchaVerifier.render().catch((err) => {
  //       console.error("Failed to render reCAPTCHA:", err);
  //     });
  //   }

  //   return () => {
  //     if (window.recaptchaVerifier) {
  //       window.recaptchaVerifier.clear();
  //       window.recaptchaVerifier = undefined;
  //     }
  //   };
  // }, []);

  // useEffect(() => {
  //   const el = document.getElementById("recaptcha-container");
  //   customLogger("Recaptcha container present:", !!el);
  // }, []);

  return (
    <div className="verify-otp-container">
      <div>
        <img src="/assets/images/lock-icon.svg" alt="" />
      </div>

      <Flex align="center" gap={20}>
        <div className="icon-container" onClick={handleBackClick}>
          <ArrowLeftOutlined />
        </div>

        <div>
          <Title className="page-title">Verify OTP</Title>
        </div>
      </Flex>

      <Text className="sub-text">
        An authentication code has been sent to {phoneNumber}
      </Text>

      <Flex gap={16}>
        {otp.map((digit, index) => (
          <Input
            key={index}
            disabled={loading}
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            className="otp-input"
            ref={(el) => {
              if (el) inputRefs.current[index] = el;
            }}
          />
        ))}
      </Flex>

      <Text className="recieve-otp" onClick={handleResend}>
        Didn't recieve OTP code? <br />
        <span className="resend">Resend OTP {timer ? `(${timer})` : ""}</span>
      </Text>

      {/* <div id="recaptcha-container"></div> */}
    </div>
  );
};

export default VerifyRegisterOTP;
